import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import './franchise.css'

const franchiseImages = Array.from({ length: 10 }, (_, i) =>
  `https://cosacademyassets.s3.us-east-2.amazonaws.com/franchise/${i + 1}.jpg`
);

const franchisePathways = [
  {
    eyebrow: "01",
    title: "Studio Franchise",
    copy:
      "Open a COSMETINK-branded PMU destination focused on premium brow, lip, and eyeliner services with an elevated client experience.",
  },
  {
    eyebrow: "02",
    title: "Academy Franchise",
    copy:
      "Bring COSMETINK education into your market with academy-backed training, standards, onboarding, and student experience systems.",
  },
  {
    eyebrow: "03",
    title: "Hybrid Location",
    copy:
      "Build the full ecosystem: client services, training classes, retail opportunities, and a brand presence designed for long-term growth.",
  },
];

const advantages = [
  {
    number: "01",
    title: "Recognizable Brand World",
    copy:
      "A luxury visual identity, polished messaging, and a client experience that feels established from day one.",
  },
  {
    number: "02",
    title: "Education-Led Systems",
    copy:
      "Academy-backed protocols, onboarding, service standards, and training frameworks built around consistency.",
  },
  {
    number: "03",
    title: "Premium Positioning",
    copy:
      "A franchise concept designed around elevated beauty, artistry, client trust, and market differentiation.",
  },
  {
    number: "04",
    title: "Expansion Pathways",
    copy:
      "Start with the model that fits your market, then grow toward studio, academy, hybrid, or product-led opportunities.",
  },
];

const candidateTraits = [
  ["Beauty-Driven Visionaries", "Inspired by aesthetics, confidence, artistry, and transformation."],
  ["Self-Motivated Builders", "Independent thinkers who can lead with initiative and long-term focus."],
  ["Detail-Oriented Leaders", "People who value precision, standards, visual harmony, and consistency."],
  ["People-First Operators", "Client-centered owners who understand trust, care, and emotional connection."],
  ["Business-Minded or Ready to Learn", "You do not need every answer yet — but you should be ready to grow, lead, and evolve."],
];

export default function FranchisePage() {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [zoomed, setZoomed] = useState(false);
  const [bgColor, setBgColor] = useState("244 62 168");
  const colorCache = useMemo(() => new Map(), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let observer;
    let cancelled = false;

    const getAverageColor = (src) => {
      if (colorCache.has(src)) return Promise.resolve(colorCache.get(src));

      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            const sampleSize = 48;
            canvas.width = sampleSize;
            canvas.height = sampleSize;
            ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
            const { data } = ctx.getImageData(0, 0, sampleSize, sampleSize);
            let r = 0;
            let g = 0;
            let b = 0;
            let count = 0;

            for (let i = 0; i < data.length; i += 4) {
              if (data[i + 3] < 200) continue;
              r += data[i];
              g += data[i + 1];
              b += data[i + 2];
              count += 1;
            }

            const color = count
              ? `${Math.round(r / count)} ${Math.round(g / count)} ${Math.round(b / count)}`
              : "244 62 168";

            colorCache.set(src, color);
            resolve(color);
          } catch {
            resolve("244 62 168");
          }
        };
        img.onerror = () => resolve("244 62 168");
        img.src = src;
      });
    };

    const handleIntersect = (entries) => {
      if (cancelled) return;
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

      if (!visible) return;
      const src = visible.target.getAttribute("src");
      if (!src) return;

      getAverageColor(src).then((color) => {
        if (!cancelled) setBgColor(color);
      });
    };

    const images = Array.from(document.querySelectorAll(".franchise-v2-lookbook-img"));
    if (images.length) {
      observer = new IntersectionObserver(handleIntersect, {
        threshold: [0.3, 0.45, 0.6, 0.75],
        rootMargin: "-12% 0px -12% 0px",
      });
      images.forEach((img) => observer.observe(img));
      getAverageColor(images[0].getAttribute("src")).then((color) => {
        if (!cancelled) setBgColor(color);
      });
    }

    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
    };
  }, [colorCache]);

  useEffect(() => {
    if (lightboxIndex === null) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight") {
        setZoomed(false);
        setLightboxIndex((prev) => (prev + 1) % franchiseImages.length);
      }
      if (e.key === "ArrowLeft") {
        setZoomed(false);
        setLightboxIndex((prev) => (prev - 1 + franchiseImages.length) % franchiseImages.length);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [lightboxIndex]);

  const openLightbox = (idx) => {
    setLightboxIndex(idx);
    setZoomed(false);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    setZoomed(false);
  };

  const showNext = (e) => {
    e.stopPropagation();
    setZoomed(false);
    setLightboxIndex((prev) => (prev + 1) % franchiseImages.length);
  };

  const showPrev = (e) => {
    e.stopPropagation();
    setZoomed(false);
    setLightboxIndex((prev) => (prev - 1 + franchiseImages.length) % franchiseImages.length);
  };

  const toggleZoom = (e) => {
    e.stopPropagation();
    setZoomed((prev) => !prev);
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>COSMETINK Franchising | Luxury PMU Studio Expansion</title>
        <link rel="canonical" href="https://www.cosmetink.com/franchise" />
        <meta
          name="description"
          content="COSMETINK is now franchising. Explore studio, academy, and hybrid franchise models and request information today."
        />
      </Helmet>

   

      <main className="franchise-v2-page" style={{ "--franchise-bg": bgColor }}>
        <section className="franchise-v2-hero" aria-label="COSMETINK franchise hero">
          <div className="franchise-v2-hero-bg" />

          <div className="franchise-v2-nav-shell">
            <a className="franchise-v2-brand" href="/">
              <span>COSMET</span>
              <b>INK</b>
              <sup>®</sup>
            </a>
            <nav className="franchise-v2-nav" aria-label="COSMETINK destinations">
              <a href="https://studio.cosmetink.com">Studio</a>
              <a href="https://academy.cosmetink.com">Academy</a>
              <a href="https://shop.cosmetink.com">Pro Shop</a>
              <a href="/franchise">Franchise</a>
            </nav>
          </div>

          <div className="franchise-v2-hero-inner">
            <div className="franchise-v2-hero-copy">
              <div className="franchise-v2-kicker">Now Franchising</div>
              <h1>Build the Next COSMETINK Destination.</h1>
              <p>
                Bring a luxury permanent makeup studio, training academy, or hybrid beauty concept into your market — backed by the COSMETINK brand standard.
              </p>

              <div className="franchise-v2-cta-row">
                <a
                  className="franchise-v2-cta franchise-v2-cta-primary"
                  href="mailto:info@cosmetink.com?subject=COSMETINK%20Franchise%20Interest"
                >
                  Request Franchise Info
                </a>
                <a className="franchise-v2-cta franchise-v2-cta-ghost" href="#franchise-pathways">
                  Explore Models
                </a>
              </div>

              <div className="franchise-v2-note">
                Include your city, experience, current business status, and preferred model.
              </div>
            </div>

            <div className="franchise-v2-hero-card" aria-label="COSMETINK franchise visual card">
              <div className="franchise-v2-card-media">
                <img src={franchiseImages[6]} alt="COSMETINK franchise studio interior" />
              </div>
              <div className="franchise-v2-card-panel">
                <span>Studio • Academy • Hybrid</span>
                <strong>A beauty brand built to scale with style.</strong>
              </div>
            </div>
          </div>
        </section>

        <section id="franchise-pathways" className="franchise-v2-pathways">
          <div className="franchise-v2-section-head">
            <div className="franchise-v2-kicker dark">Franchise Pathways</div>
            <h2>Choose the model that fits your market.</h2>
            <p>
              Whether you already operate a beauty space or want to launch a full COSMETINK destination, the franchise structure can be built around your vision.
            </p>
          </div>

          <div className="franchise-v2-pathway-grid">
            {franchisePathways.map((pathway) => (
              <article className="franchise-v2-pathway-card" key={pathway.title}>
                <span>{pathway.eyebrow}</span>
                <h3>{pathway.title}</h3>
                <p>{pathway.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="franchise-v2-advantage">
          <div className="franchise-v2-advantage-inner">
            <div className="franchise-v2-section-head left">
              <div className="franchise-v2-kicker dark">The COSMETINK Advantage</div>
              <h2>Built for artists, entrepreneurs, and investors ready to build something iconic.</h2>
              <p>
                Step into a recognizable luxury beauty ecosystem with education, standards, positioning, and expansion pathways designed to help you build something bigger than a service menu.
              </p>
            </div>

            <div className="franchise-v2-advantage-grid">
              {advantages.map((item) => (
                <article className="franchise-v2-advantage-card" key={item.title}>
                  <span>{item.number}</span>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="franchise-v2-lookbook" aria-label="COSMETINK franchise lookbook">
          <div className="franchise-v2-lookbook-head">
            <div>

              <h2>The world already exists. Now we scale it.</h2>
               </div>
            <p>
              A premium visual environment, client journey, and education-forward brand experience designed to feel unmistakably COSMETINK.
            </p>
          </div>
       

    {/*      <div className="franchise-v2-lookbook-grid">
            {franchiseImages.slice(0, 8).map((src, idx) => (
              <button
                className={`franchise-v2-lookbook-item item-${idx + 1}`}
                key={src}
                type="button"
                onClick={() => openLightbox(idx)}
              >
                <img
                  className="franchise-v2-lookbook-img"
                  src={src}
                  alt={`COSMETINK franchise lookbook ${idx + 1}`}
                  loading={idx < 2 ? "eager" : "lazy"}
                />
                <span>View</span>
              </button>
            ))}
          </div> */}
        </section>

        <section className="franchise-v2-candidate">
          <div className="franchise-v2-candidate-media">
            <img src="https://cosmetinklanding.s3.us-east-2.amazonaws.com/DSC07421.jpeg" alt="COSMETINK artist candidate" />
            <div>
              <span>Who This Is For</span>
              <strong>The Ideal Candidate</strong>
            </div>
          </div>

          <div className="franchise-v2-candidate-panel">
            <div className="franchise-v2-kicker dark">Owner Energy</div>
            <h2>We look for spark, taste, discipline, and care.</h2>
            <p className="franchise-v2-candidate-intro">
              Success does not come from a checklist. It comes from the person behind the brand — someone who wants to lead, elevate, and build with intention.
            </p>

            <div className="franchise-v2-trait-list">
              {candidateTraits.map(([title, copy]) => (
                <div className="franchise-v2-trait" key={title}>
                  <strong>{title}</strong>
                  <span>{copy}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="franchise-v2-final">
          <div className="franchise-v2-final-inner">
            <div className="franchise-v2-kicker">Private Franchise Inquiry</div>
            <h2>Let’s build something iconic.</h2>
            <p>
              We’re looking for artists and entrepreneurs who want a premium brand, a proven education system, and a beautiful client experience. Reach out to request model details, pricing, and support options.
            </p>
            <a
              className="franchise-v2-cta franchise-v2-cta-primary"
              href="mailto:info@cosmetink.com?subject=COSMETINK%20Franchise%20Inquiry"
            >
              Start the Conversation
            </a>
          </div>
        </section>
      </main>

      {lightboxIndex !== null && (
        <div className="franchise-v2-lightbox" role="dialog" aria-modal="true" onClick={closeLightbox}>
          <button className="franchise-v2-lightbox-close" type="button" onClick={closeLightbox}>
            Close
          </button>

          <div className="franchise-v2-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button className="franchise-v2-lightbox-nav" type="button" onClick={showPrev}>
              Prev
            </button>

            <div className={`franchise-v2-lightbox-media ${zoomed ? "zoomed" : ""}`} onClick={toggleZoom}>
              <img
                className={zoomed ? "zoomed" : ""}
                src={franchiseImages[lightboxIndex]}
                alt={`COSMETINK franchise slide ${lightboxIndex + 1}`}
              />
              <span>Click to {zoomed ? "zoom out" : "zoom in"}</span>
            </div>

            <button className="franchise-v2-lightbox-nav" type="button" onClick={showNext}>
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
}
